import _ from 'lodash';
import { faBriefcase } from '@fortawesome/pro-regular-svg-icons/faBriefcase';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Range } from 'slate';
import React, { useCallback, useContext, useState } from 'react';
import styled from 'styled-components';
import { usePlateEditorState } from '@udecode/plate';

import AgentAssistanceConfigContext from 'components/contexts/agent_assistance_config_context';
import i18n from 'scripts/i18n/i18n';
import AIFriendlyIcon from 'components/lib/icons/ai_friendly';
import AIRephraseIcon from 'components/lib/icons/ai_rephrase';
import AILengthenIcon from 'components/lib/icons/ai_lengthen';
import AIShortenIcon from 'components/lib/icons/ai_shorten';
import AITranslateIcon from 'components/lib/icons/ai_translate';
import AITextCompletion from 'models/ai_text_completion';
import AITextCompletionContext from 'components/text_editor_new/plugins/ai/components/ai_text_completion_context';
import { Item } from 'components/text_editor_new/components/controls/controls_new';
import { Separator } from 'components/composer/shared/editor_controls_new';
import Tooltip from 'components/common/tooltip';
import { useDebounced } from 'components/hooks/debounce_hooks';
import { usePopoverMenu } from 'components/common/menu';
import useToggleAICompletion from 'components/text_editor_new/plugins/ai/ai_text_completion/use_toggle_ai_completion';
import AiTranslateIntoContextMenu from 'components/text_editor_new/plugins/ai/components/ai_translate_into_context_menu';

const TOOLTIP_DELAY = 200;

export default function AITextRewriteControls() {
  const agentAssistanceConfig = useContext(AgentAssistanceConfigContext);
  const includeAIFeatures = !agentAssistanceConfig.disableHeroAI;
  if (!includeAIFeatures) {
    return null;
  }

  return (
    <>
      <AITextCompletionButtons />
      <Separator />
    </>
  );
}

export function AITextCompletionButtons() {
  const editor = usePlateEditorState();
  const props = {
    isCollapsed: editor.selection && Range.isCollapsed(editor.selection),
    isDisabled: editor.shouldDisableTextCompletion(),
  };

  return (
    <>
      <AIMakeFriendly {...props} />
      <AIMakeProfessional {...props} />
      <AIRephrase {...props} />
      <AIShorten {...props} />
      <AILengthen {...props} />
      <AITranslationButton {...props} />
    </>
  );
}

export function AITranslationButton(props) {
  const [isTranslateIntoMenuVisible, setTranslateIntoMenuVisible] = useState(false);
  const { targetLanguage } = useContext(AITextCompletionContext);
  const { targetRef, setTargetRef } = usePopoverMenu();
  const translate = useToggleAICompletion(AITextCompletion.Types.translate);

  return (
    <>
      <Separator />
      <AITranslate {...props} innerRef={setTargetRef} onClick={handleClick} />
      <AiTranslateIntoContextMenu
        handleSelectLanguage={handleSelectLanguage}
        isOpen={isTranslateIntoMenuVisible}
        lastSelectedLanguage={targetLanguage}
        onClose={closeTranslateIntoMenu}
        position="top"
        targetRef={targetRef}
      />
    </>
  );

  function closeTranslateIntoMenu() {
    setTranslateIntoMenuVisible(false);
  }

  function openTranslateIntoMenu() {
    setTranslateIntoMenuVisible(true);
  }

  function handleClick() {
    if (targetLanguage) {
      return translate({ targetLanguage });
    }

    openTranslateIntoMenu();
  }

  function handleSelectLanguage(targetLanguage) {
    translate({ targetLanguage });
  }
}

export const AITranslate = withAIRewriteButton({
  'data-aid': 'aiTranslateButton',
  renderIcon: ({ isDisabled }) => <StyledTranslateIcon $isDisabled={isDisabled} />,
  type: AITextCompletion.Types.translate,
});

const AIMakeFriendly = withAIRewriteButton({
  'data-aid': 'aiMakeFriendlyButton',
  renderIcon: ({ isDisabled }) => <StyledFriendlyIcon $isDisabled={isDisabled} />,
  type: AITextCompletion.Types.make_friendly,
});

const AIMakeProfessional = withAIRewriteButton({
  'data-aid': 'aiMakeProfessionalButton',
  renderIcon: () => <FontAwesomeIcon icon={faBriefcase} />,
  type: AITextCompletion.Types.make_professional,
});

const AIRephrase = withAIRewriteButton({
  'data-aid': 'aiRephraseButton',
  renderIcon: ({ isDisabled }) => <StyledRephraseIcon $isDisabled={isDisabled} />,
  type: AITextCompletion.Types.rephrase,
});

const AIShorten = withAIRewriteButton({
  'data-aid': 'aiShortenButton',
  renderIcon: ({ isDisabled }) => <StyledShortenIcon $isDisabled={isDisabled} />,
  type: AITextCompletion.Types.make_shorter,
});

const AILengthen = withAIRewriteButton({
  'data-aid': 'aiLengthenButton',
  renderIcon: ({ isDisabled }) => <StyledLengthenIcon $isDisabled={isDisabled} />,
  type: AITextCompletion.Types.make_longer,
});

export { AIMakeFriendly, AIMakeProfessional, AIRephrase, AIShorten, AILengthen };

const StyledFriendlyIcon = styled(AIFriendlyIcon)`
  stroke: ${p => (p.$isDisabled ? p.theme.colors.gray300 : p.theme.colors.black)};
`;

const StyledRephraseIcon = styled(AIRephraseIcon)`
  stroke: ${p => (p.$isDisabled ? p.theme.colors.gray300 : p.theme.colors.black)};
`;

const StyledLengthenIcon = styled(AILengthenIcon)`
  stroke: ${p => (p.$isDisabled ? p.theme.colors.gray300 : p.theme.colors.black)};
`;

const StyledShortenIcon = styled(AIShortenIcon)`
  stroke: ${p => (p.$isDisabled ? p.theme.colors.gray300 : p.theme.colors.black)};
`;

const StyledTranslateIcon = styled(AITranslateIcon)`
  stroke: ${p => (p.$isDisabled ? p.theme.colors.gray300 : p.theme.colors.black)};
`;

export function withAIRewriteButton({ 'data-aid': dataAid, renderIcon, type }) {
  function Component({ className, isCollapsed, isDisabled, onClick, innerRef }) {
    const { completion } = useContext(AITextCompletionContext);
    const isActive = completion?.type === type;
    const toggleAICompletion = useToggleAICompletion(type);

    const handleClick = useDebounced(onClick ?? toggleAICompletion, 50, {});

    const [itemProps, tooltipProps] = useTemporarilyHideTooltipOnMouseDown(isDisabled);
    const tooltipMessage = getTooltipMessage(type, isDisabled, isCollapsed);

    return (
      <Tooltip delay={TOOLTIP_DELAY} message={tooltipMessage} {...tooltipProps}>
        <Item
          className={className}
          data-aid={dataAid}
          data-disabled={isDisabled}
          isActive={isActive}
          isDisabled={isDisabled}
          onClick={isDisabled ? _.noop : handleClick}
          ref={innerRef}
          {...itemProps}
        >
          {renderIcon({ isDisabled })}
        </Item>
      </Tooltip>
    );
  }

  Component.displayName = `AIRewriteButton(${_.upperFirst(_.startCase(type))})`;
  return Component;
}

// When the user clicks on the button, we want to hide the tooltip. This is partially
// to get around a _popover related bug where the tooltip starts soaring off into space
// when the button gets clicked and the preview is shown but.. it's also just a better
// user experience IMO.
//
// We do want to keep the tooltip visible when the button is disabled, since it tells them
// why it's not clickable when it's disabled.
function useTemporarilyHideTooltipOnMouseDown(isDisabled) {
  const [isVisible, setIsVisible] = useState(true);

  const onMouseDown = useCallback(
    evt => {
      if (!isDisabled) {
        setIsVisible(false);
        evt.preventDefault();
      }
    },
    [isDisabled]
  );

  // It might seem like the tooltip should become visible again on mouse up,
  // but internally the tooltip waits for another mouse enter event before it
  // becomes visible again.
  const onMouseUp = useCallback(() => {
    if (!isDisabled) {
      setIsVisible(true);
    }
  }, [isDisabled]);

  const finalIsVisible = isDisabled ? undefined : isVisible;

  let itemProps = { onMouseDown, onMouseUp };
  let tooltipProps = { isVisible: finalIsVisible };
  return [itemProps, tooltipProps];
}

// Literal i18n.t() calls (evaluated per render, not at module load) so the
// tooltips re-translate on language change and i18next-parser can see them.
function getTooltipMessage(type, isDisabled, isCollapsed) {
  if (isDisabled) {
    return i18n.t('Please write something first');
  }

  if (type === AITextCompletion.Types.make_friendly) {
    return isCollapsed ? i18n.t('Make all text more friendly') : i18n.t('Make selected text more friendly');
  } else if (type === AITextCompletion.Types.make_professional) {
    return isCollapsed ? i18n.t('Make all text more professional') : i18n.t('Make selected text more professional');
  } else if (type === AITextCompletion.Types.rephrase) {
    return isCollapsed ? i18n.t('Rephrase all text') : i18n.t('Rephrase selected text');
  } else if (type === AITextCompletion.Types.make_shorter) {
    return isCollapsed ? i18n.t('Make all text shorter') : i18n.t('Make selected text shorter');
  } else if (type === AITextCompletion.Types.make_longer) {
    return isCollapsed ? i18n.t('Make all text longer') : i18n.t('Make selected text longer');
  } else if (type === AITextCompletion.Types.translate) {
    // Translation only works on the entire message.
    return i18n.t('Translate all the text');
  } else {
    return '';
  }
}
